import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../api/client';
import { getZones, getEvents, GxZone } from '../../api/guestExperience';
import {
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_SURFACE,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';

interface ZoneSubscription {
  id: string;
  eventGroupId: string;
  gxZoneId: string;
  subscribedAt: string;
  gxZone?: {
    id: string;
    name: string;
    color: string;
    eventId: string;
  };
}

interface GroupZoneSubscriptionsProps {
  groupId: string;
}

const iconBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 6px',
  fontSize: '0.9rem',
  borderRadius: '4px',
  lineHeight: 1,
  transition: 'background-color 0.15s',
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1.25rem',
    paddingTop: '1rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  heading: {
    fontFamily: FONT_HEADING,
    fontWeight: 600,
    fontSize: '0.9rem',
    color: TEXT_DARK,
    margin: 0,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  zoneItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.4rem 0.6rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    fontSize: '0.85rem',
  },
  zoneInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  zoneDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  zoneName: {
    color: TEXT_DARK,
    fontWeight: 500,
  },
  empty: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
    padding: '0.5rem 0',
  },
  addSection: {
    marginTop: '0.6rem',
    position: 'relative' as const,
  },
  selectWrapper: {
    position: 'relative' as const,
  },
  select: {
    width: '100%',
    padding: '0.45rem 0.6rem',
    fontSize: '0.82rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: TEXT_DARK,
    cursor: 'pointer',
    appearance: 'none' as const,
    paddingRight: '1.5rem',
  },
  selectArrow: {
    position: 'absolute' as const,
    right: '0.5rem',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none' as const,
    fontSize: '0.7rem',
    color: TEXT_MUTED,
  },
  error: {
    color: '#d32f2f',
    fontSize: '0.78rem',
    marginTop: '0.4rem',
  },
  loading: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    padding: '0.5rem 0',
  },
};

export function GroupZoneSubscriptions({ groupId }: GroupZoneSubscriptionsProps) {
  const [subscriptions, setSubscriptions] = useState<ZoneSubscription[]>([]);
  const [availableZones, setAvailableZones] = useState<GxZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const selectRef = useRef<HTMLSelectElement>(null);

  async function fetchSubscriptions() {
    try {
      setLoading(true);
      const data = await apiClient<ZoneSubscription[]>(`/event-groups/${groupId}/zones`);
      setSubscriptions(data);
    } catch {
      setError('Failed to load zone subscriptions.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchAvailableZones() {
    try {
      setZonesLoading(true);
      // Fetch events, then get zones for all events
      const events = await getEvents();
      if (events.length === 0) {
        setAvailableZones([]);
        return;
      }
      // Fetch zones from all events and merge
      const allZones: GxZone[] = [];
      for (const event of events) {
        try {
          const zones = await getZones(event.id);
          allZones.push(...zones);
        } catch {
          // Skip events where we can't fetch zones
        }
      }
      // Only show active zones
      setAvailableZones(allZones.filter((z) => z.active));
    } catch {
      setAvailableZones([]);
    } finally {
      setZonesLoading(false);
    }
  }

  useEffect(() => {
    fetchSubscriptions();
    fetchAvailableZones();
  }, [groupId]);

  async function handleSubscribe(gxZoneId: string) {
    if (!gxZoneId) return;
    try {
      setActionLoading(gxZoneId);
      setError('');
      await apiClient(`/event-groups/${groupId}/zones`, {
        method: 'POST',
        body: { gxZoneId },
      });
      await fetchSubscriptions();
      // Reset select
      if (selectRef.current) {
        selectRef.current.value = '';
      }
    } catch (err: any) {
      const message = err?.message || 'Failed to subscribe to zone.';
      setError(message);
    } finally {
      setActionLoading(null);
    }
  }

  async function handleUnsubscribe(subscriptionId: string) {
    try {
      setActionLoading(subscriptionId);
      setError('');
      await apiClient(`/event-groups/${groupId}/zones/${subscriptionId}`, {
        method: 'DELETE',
      });
      await fetchSubscriptions();
    } catch {
      setError('Failed to unsubscribe from zone.');
    } finally {
      setActionLoading(null);
    }
  }

  // Filter out zones already subscribed
  const subscribedZoneIds = new Set(subscriptions.map((s) => s.gxZoneId));
  const unsubscribedZones = availableZones.filter((z) => !subscribedZoneIds.has(z.id));

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.heading}>Zone Subscriptions</h4>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <div style={styles.loading}>Loading subscriptions…</div>
      ) : subscriptions.length === 0 ? (
        <div style={styles.empty}>No zone subscriptions yet.</div>
      ) : (
        <div style={styles.list}>
          {subscriptions.map((sub) => {
            const zoneName = sub.gxZone?.name || availableZones.find((z) => z.id === sub.gxZoneId)?.name || 'Unknown Zone';
            const zoneColor = sub.gxZone?.color || availableZones.find((z) => z.id === sub.gxZoneId)?.color || '#999';
            return (
              <div key={sub.id || sub.gxZoneId} style={styles.zoneItem}>
                <div style={styles.zoneInfo}>
                  <span style={{ ...styles.zoneDot, backgroundColor: zoneColor }} />
                  <span style={styles.zoneName}>{zoneName}</span>
                </div>
                <button
                  style={iconBtn}
                  onClick={() => handleUnsubscribe(sub.id || sub.gxZoneId)}
                  disabled={actionLoading === (sub.id || sub.gxZoneId)}
                  title="Unsubscribe"
                  aria-label={`Unsubscribe from ${zoneName}`}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add zone dropdown */}
      <div style={styles.addSection}>
        {zonesLoading ? (
          <div style={styles.loading}>Loading available zones…</div>
        ) : unsubscribedZones.length > 0 ? (
          <div style={styles.selectWrapper}>
            <select
              ref={selectRef}
              style={styles.select}
              defaultValue=""
              onChange={(e) => handleSubscribe(e.target.value)}
              disabled={actionLoading !== null}
              aria-label="Add zone subscription"
            >
              <option value="" disabled>
                + Add zone subscription…
              </option>
              {unsubscribedZones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
            <span style={styles.selectArrow}>▼</span>
          </div>
        ) : !loading && subscriptions.length > 0 && availableZones.length > 0 ? (
          <div style={styles.empty}>All available zones are subscribed.</div>
        ) : null}
      </div>
    </div>
  );
}
